import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import roscoImg from '../../assets/images/RoscoTransparente.gif';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'http://localhost:4000';

/**
 * Componente de recuperación de contraseña.
 * Permite al usuario solicitar un enlace de restablecimiento de contraseña por email.
 * 
 * @returns {JSX.Element} Formulario de recuperación de contraseña
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/v1/auth/forgot-password`, { email });
      setMessage(response.data.message);
      setEmail('');
    } catch (err) {
      setError(err.response?.data?.message || 'Error al procesar la solicitud');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-gray-50 overflow-hidden relative">
      {/* Rosco de fondo - centrado respecto al formulario (lado derecho) */}
      <div className="absolute inset-y-0 right-0 w-full lg:w-1/2 flex items-center justify-center pointer-events-none z-0">
        <img 
          src={roscoImg} 
          alt="Rosco FAECA Background" 
          className="w-[600px] h-[600px] object-contain opacity-35"
        />
      </div>

      {/* Lado izquierdo - Espacio en blanco (opcional: puedes agregar más contenido aquí) */}
      <div className="hidden lg:flex lg:w-1/2 bg-white items-center justify-center relative overflow-hidden z-10">
        <div className="text-center px-8">
          <img 
            src={roscoImg} 
            alt="Rosco FAECA" 
            className="w-48 h-48 object-contain mx-auto mb-6 opacity-90"
          />
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Recuperación de Contraseña
          </h2>
          <p className="text-gray-600 text-lg">
            Te enviaremos un enlace seguro para restablecer tu contraseña
          </p>
        </div>
      </div>

      {/* Lado derecho - Formulario */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 md:p-8 relative z-10">
        <div className="w-full max-w-md">
          {/* Header minimalista */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
              ¿Olvidaste tu contraseña?
            </h1>
            <p className="text-gray-500">
              Ingresa tu email y te enviaremos un enlace para recuperarla
            </p>
          </div>

          {/* Mensajes de éxito */}
          {message && (
            <div className="bg-green-50 border-l-4 border-green-500 text-green-700 px-4 py-3 rounded mb-6">
              <div className="flex items-center gap-2">
                <i className="fas fa-check-circle"></i>
                <span className="text-sm">{message}</span>
              </div>
            </div>
          )}

          {/* Mensajes de error */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded mb-6">
              <div className="flex items-center gap-2">
                <i className="fas fa-exclamation-circle"></i>
                <span className="text-sm">{error}</span>
              </div>
            </div>
          )}

          {/* Formulario */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Correo electrónico
              </label>
              <input
                type="email"
                placeholder="correo@ejemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent transition"
                required
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gray-800 text-white py-3 rounded-lg hover:bg-gray-900 transition font-medium shadow-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {loading ? 'Enviando...' : 'Enviar enlace de recuperación'}
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <div className="text-center">
              <Link
                to="/login"
                className="text-gray-600 hover:text-gray-800 font-medium"
              >
                ← Volver al inicio de sesión
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

